import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useRealtimeJobs } from '../../hooks/useRealtimeJobs';
import { useRealtimeResumes } from '../../hooks/useRealtimeResumes';
import { DashboardPage } from './DashboardPage';
import { GuidedIngestionPage } from './GuidedIngestionPage';

const GUIDED_SEEN_KEY_PREFIX = 'xrank_guided_ingestion_seen_v1:';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { org } = useTenant();
  const { jobs, loading: jobsLoading } = useRealtimeJobs(org?.orgId);
  const { resumes, loading: resumesLoading } = useRealtimeResumes(org?.orgId);
  const [guidedSeen, setGuidedSeen] = useState(false);

  const guidedSeenKey = useMemo(() => {
    if (!org?.orgId) return null;
    return `${GUIDED_SEEN_KEY_PREFIX}${org.orgId}`;
  }, [org?.orgId]);

  useEffect(() => {
    if (!guidedSeenKey) {
      setGuidedSeen(false);
      return;
    }

    try {
      setGuidedSeen(localStorage.getItem(guidedSeenKey) === '1');
    } catch {
      setGuidedSeen(false);
    }
  }, [guidedSeenKey]);

  const markGuidedSeen = useCallback(() => {
    if (!guidedSeenKey) return;

    try {
      localStorage.setItem(guidedSeenKey, '1');
    } catch {
      // Ignore localStorage failures and keep in-memory state aligned.
    }
    setGuidedSeen(true);
  }, [guidedSeenKey]);

  if (jobsLoading || resumesLoading) {
    return (
      <div className="v2-page">
        <div className="v2-empty"><span className="v2-spinner" /> Loading workspace...</div>
      </div>
    );
  }

  const shouldShowGuided = Boolean(org?.orgId)
    && jobs.length === 0
    && resumes.length === 0
    && !guidedSeen;

  if (shouldShowGuided) {
    return (
      <GuidedIngestionPage
        onStartJob={() => {
          markGuidedSeen();
          navigate('/jobs/new?entry=guided');
        }}
        onStartResume={() => {
          markGuidedSeen();
          navigate('/talent?entry=guided');
        }}
        onSkip={() => {
          markGuidedSeen();
        }}
      />
    );
  }

  return <DashboardPage />;
};
